import { useCallback, useEffect, useRef, useState } from 'react'

// The "thinking energy" gating mechanic. Energy (0–100) drains while the player is
// silent and refills while they speak. At/below `dimThreshold` the puzzle dims/blurs
// — a SOFT cue, never a hard timeout: the player always recovers by speaking again.
//
// This is the documented A/B seam. The eventual experiment swaps ENERGY_CONFIG (or
// picks a variant per session) to find the drain/refill curve that yields the best
// genuine-verbalisation-to-strategic-noise ratio. Keep all tunables here.
export type EnergyConfig = {
  startEnergy: number
  maxEnergy: number
  decayPerSec: number // drain rate while silent
  refillPerSec: number // refill rate while speaking
  activeWindowMs: number // how long after the last speech activity we still count as "speaking"
  dimThreshold: number // energy at/below which the puzzle dims
  tickMs: number // simulation + sampling cadence
}

export const ENERGY_CONFIG: EnergyConfig = {
  startEnergy: 100,
  maxEnergy: 100,
  decayPerSec: 12, // ~8s of silence from full to empty
  refillPerSec: 40, // a couple of seconds of talking refills it
  activeWindowMs: 1200,
  dimThreshold: 25,
  tickMs: 200,
}

export type EnergySample = { ts: number; energy: number; speaking: boolean }

export type UseEnergyOptions = {
  running: boolean // only simulate while the puzzle is being played
  tsBase: number // performance-now origin for sample timestamps
  config?: EnergyConfig
  onSample?: (sample: EnergySample) => void // for the event capture stream
  resetKey?: number | string // when this changes, energy resets to full (new puzzle)
}

export type EnergyState = {
  energy: number
  dimmed: boolean
  speaking: boolean
  registerActivity: () => void // call on each speech transcript / speech-start
}

export const useThinkingEnergy = (opts: UseEnergyOptions): EnergyState => {
  const { running, tsBase, config = ENERGY_CONFIG, onSample, resetKey } = opts

  const [energy, setEnergy] = useState(config.startEnergy)
  const [speaking, setSpeaking] = useState(false)

  const energyRef = useRef(config.startEnergy)
  const lastActivityAtRef = useRef<number>(-Infinity)
  const cfgRef = useRef(config)
  cfgRef.current = config
  const onSampleRef = useRef(onSample)
  onSampleRef.current = onSample

  const registerActivity = useCallback(() => {
    lastActivityAtRef.current = performance.now()
  }, [])

  // Reset to full energy at the start of each new puzzle (resetKey change).
  useEffect(() => {
    energyRef.current = cfgRef.current.startEnergy
    lastActivityAtRef.current = -Infinity
    setEnergy(cfgRef.current.startEnergy)
    setSpeaking(false)
  }, [resetKey])

  useEffect(() => {
    if (!running) return
    const cfg = cfgRef.current
    const id = window.setInterval(() => {
      const now = performance.now()
      const isSpeaking = now - lastActivityAtRef.current < cfg.activeWindowMs
      const dt = cfg.tickMs / 1000
      let next = energyRef.current + (isSpeaking ? cfg.refillPerSec : -cfg.decayPerSec) * dt
      next = Math.max(0, Math.min(cfg.maxEnergy, next))
      energyRef.current = next
      setEnergy(next)
      setSpeaking(isSpeaking)
      onSampleRef.current?.({ ts: Math.round(now - tsBase), energy: Math.round(next), speaking: isSpeaking })
    }, cfg.tickMs)
    return () => window.clearInterval(id)
  }, [running, tsBase])

  return {
    energy,
    dimmed: energy <= config.dimThreshold,
    speaking,
    registerActivity,
  }
}
